import React, { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { PlusIcon } from '@heroicons/react/24/outline';
import FormCreator from './components_forms_creator';
import { ActionBar, FormsTable, FormData } from './components';
import { LIST_FORMS } from '~/services/forms';

const FormsHome = () => {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [searchValue, setSearchValue] = useState('');

  // Query para obtener los formularios reales
  const { data: formsData, loading, error, refetch } = useQuery(LIST_FORMS, {
    variables: {
      data: {
        agent_id: agentId
      }
    },
    skip: !agentId,
    fetchPolicy: 'cache-and-network'
  });

  // Transformar los datos de la API al formato esperado por la tabla
  const transformFormData = (apiForm: any): FormData => {
    return {
      id: apiForm._id || apiForm.id,
      name: apiForm.name || 'Sin nombre',
      description: apiForm.description || '',
      fieldsCount: apiForm.fields?.length || 0,
      aiCompleted: apiForm.aiCompleted ?? false, // Usar aiCompleted directamente (sin guión bajo)
      createdAt: apiForm.created_at || apiForm.createdAt || new Date().toISOString(),
      updatedAt: apiForm.updated_at || apiForm.updatedAt || new Date().toISOString(),
      status: apiForm.status || 'draft'
    };
  };

  // Procesar los datos de la respuesta
  const forms: FormData[] = formsData?.list_forms?.data 
    ? (Array.isArray(formsData.list_forms.data) 
        ? formsData.list_forms.data.map(transformFormData)
        : [])
    : [];

  const handleCreateForm = () => {
    // Navegar al creador de formularios
    navigate(`/agent/${agentId}/forms/create`);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleEditForm = (formId: string) => {
    // Navegar al editor de formularios
    navigate(`/agent/${agentId}/forms/edit/${formId}`);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      // Opcional: mostrar mensaje de éxito
      console.log('Formularios actualizados exitosamente');
    } catch (error) {
      console.error('Error al actualizar formularios:', error);
    }
  };

  // Filter forms based on search
  const filteredForms = forms.filter(form =>
    form.name.toLowerCase().includes(searchValue.toLowerCase()) ||
    form.description.toLowerCase().includes(searchValue.toLowerCase())
  );

  const hasForms = forms.length > 0;

  // Manejar errores
  if (error) {
    console.error('Error loading forms:', error);
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('forms_title', 'Formularios')}
          </h1>
          <p className="text-gray-600 max-w-2xl">
            {t('forms_description', 'Crea y gestiona formularios personalizados para recopilar información de tus usuarios. Configura campos, validaciones y integraciones de manera sencilla.')}
          </p>
        </div>

        {/* Action Bar */}
        <ActionBar
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          onCreateClick={handleCreateForm}
          onRefresh={handleRefresh}
          isRefreshing={loading}
        />

        {/* Content */}
        {hasForms ? (
          /* Forms Table */
          <FormsTable
            data={filteredForms}
            isLoading={loading}
            onCreateForm={handleCreateForm}
            onEditForm={handleEditForm}
          />
        ) : (
          /* Empty State - Create Form Card */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            {loading ? (
              /* Loading State */
              <div className="animate-pulse">
                <div className="h-16 w-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4 mx-auto mb-6"></div>
                <div className="h-10 bg-gray-200 rounded w-32 mx-auto"></div>
              </div>
            ) : error ? (
              /* Error State */
              <>
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-red-600 text-2xl">⚠️</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Error al cargar formularios
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Hubo un problema al cargar los formularios. Intenta nuevamente.
                </p>
                <button
                  onClick={() => refetch()}
                  className="btn btn-outline inline-flex items-center gap-2 px-6 py-3 mr-2"
                >
                  Reintentar
                </button>
                <button
                  onClick={handleCreateForm}
                  className="btn btn-primary inline-flex items-center gap-2 px-6 py-3"
                >
                  <PlusIcon className="w-5 h-5" />
                  {t('create_form', 'Crear Formulario')}
                </button>
              </>
            ) : (
              /* Empty State */
              <>
                <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <PlusIcon className="w-8 h-8 text-blue-600" />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('create_first_form', 'Crear tu primer formulario')}
                </h3>
                
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {t('create_form_description', 'Comienza creando un formulario personalizado para recopilar la información que necesitas de tus usuarios.')}
                </p>
                
                <button
                  onClick={handleCreateForm}
                  className="btn btn-primary inline-flex items-center gap-2 px-6 py-3"
                >
                  <PlusIcon className="w-5 h-5" />
                  {t('create_form', 'Crear Formulario')}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Forms = () => {
  return (
    <Routes>
      <Route path="/" element={<FormsHome />} />
      <Route path="/create" element={<FormCreator />} />
      <Route path="/edit/:formId" element={<FormCreator />} />
    </Routes>
  );
};

export default Forms;